import React, { useEffect, useState } from "react";
import { Box, Button, Typography, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import StackedBarChart from "../StackedBarChart/StackedBarChart";
import AgingTable from "../AgingTable/AgingTable";
import TrendLineChartForBu from "../LineChart/TrendLineChartForBu";
import StackedBarChartForteamReview from "../StackedBarChart/StackedBarChartForteamReview";
import StkdChartForManualReviewByTeam from "../StackedBarChart/StkdChartForManualReviewByTeam";
import { CircularProgress } from "@mui/material";
import { useGetTeamsListsDashboardTeamsList } from "../../hooks/Reporting";

const complexityTypes = [
    { value: "Single_Po", label: "Single PO" },
    { value: "Multi_Po", label: "Multi PO" },
    { value: "Non_Po", label: "Non PO" },
    { value: "Yet_To_Be_Extracted", label: "Yet To Be Extracted" },
];

const rejectionTypes = [
    { value: "reject", label: "Reject" },
    { value: "complete", label: "Complete" },
];


export default function TeamsAndReviewDashBoard({ data, onBuReportFilterApply, onClear, initialFilters, isPending, invoiceStatusListData }) {
    const [businessUnitRptFilters, setBusinessUnitRptFilters] = useState({
        team: initialFilters.team || [],
        complexity_type: initialFilters.complexity_type || [],
        manual_review_outcome: initialFilters.manual_review_outcome || []
    })
    const [invoicesByTeam, setInvoicesByTeam] = useState([])
    const [trendsOfInvByTeam, setTrendsOfInvByTeam] = useState([])
    const [reviewExceptionList, setReviewExceptionList] = useState([])
    const [teamRejectionRate, setTeamRejectionRate] = useState([])
    const [teamManualReviewOutcome, setTeamManualReviewOutcome] = useState([])
    const { data: teamList } = useGetTeamsListsDashboardTeamsList()

    const uniqueTeams = Array.isArray(data?.invoices_by_team)
        ? data.invoices_by_team.reduce((acc, curr) => {
            if (!acc.includes(curr.team)) {
                acc.push(curr.team);
            }
            return acc;
        }, [])
        : [];



    useEffect(() => {
        setBusinessUnitRptFilters({
            team: initialFilters?.team || [],
            complexity_type: initialFilters?.complexity_type || [],
            manual_review_outcome: initialFilters?.manual_review_outcome || []
        })
    }, [initialFilters])

    const handleChange = (event) => {
        setBusinessUnitRptFilters((prev) => {
            return {
                ...prev,
                [event.target.name]: event.target.value
            }
        })
    }
    const handleApply = () => {
        onBuReportFilterApply(businessUnitRptFilters)
    }

    const handleReset = () => {
        setBusinessUnitRptFilters({
            team: [],
            complexity_type: [],
            manual_review_outcome: []

        })
        onClear && onClear(3);
    }

    useEffect(() => {
        if (data?.rejection_rate_by_team.length) {
            setTeamRejectionRate(data?.rejection_rate_by_team)
        }
    }, [data?.rejection_rate_by_team])
    useEffect(() => {
        if (data?.manual_review_outcomes_by_team.length) {
            setTeamManualReviewOutcome(data?.manual_review_outcomes_by_team)
        }
    }, [data?.manual_review_outcomes_by_team])

    useEffect(() => {
        if (data?.invoices_by_team.length) {
            setInvoicesByTeam(data?.invoices_by_team)
        }
    }, [data?.invoices_by_team])

    useEffect(() => {
        if (data?.reviewer_exceptions.length) {
            setReviewExceptionList(data?.reviewer_exceptions)
        } else {
            setReviewExceptionList([])
        }
    }, [data?.reviewer_exceptions])



    useEffect(() => {
        if (data?.sla_compliance_trend.length) {
            setTrendsOfInvByTeam(data?.sla_compliance_trend)
        }
    }, [data?.sla_compliance_trend])


    const series = [
        {
            dataKey: "count",
            label: "Invoices",
            color: "#3C2676"
        }
    ];

    const buRejRateSeries = [
        {
            dataKey: "auto_rejections_pct",
            label: "Automatic rejection",
            color: "#5D3EA8", // dark purple
            stack: 'rejection', // stack both
        },
        {
            dataKey: "manual_rejections_pct",
            label: "Manual rejection",
            color: "#A89EDB", // lighter purple (adjust as needed)
            stack: 'rejection',
        }
    ];

    const invStatusPerBuSeries = [
        {
            dataKey: "Rejected",
            label: "Rejected",
            color: "#3A238C",
            stack: 'status',
        },
        {
            dataKey: "Processed",
            label: "Processed",
            color: "#543EA3",
            stack: 'status',
        },
        {
            dataKey: "Pending",
            label: "Pending",
            color: "#6754AD",
            stack: 'status',
        },
    ];
    const coloursByStatusDonut = ['#A296CC', '#9384C7',
        '#7B6AB8', '#6754AD'
    ];
    const capitalizeWords = (value: string) => {
        if (!value) return "";

        const acronyms = ["PO", "BU", "ERP"]; // Add more acronyms here as needed

        return value
            .replace(/_/g, " ")
            .split(" ")
            .map((word) => {
                const upperWord = word.toUpperCase();
                if (acronyms.includes(upperWord)) {
                    return upperWord;
                }
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
            })
            .join(" ");
    };

    const columns = [
        {
            field: 'reviewer',
            headerName: 'Reviewer',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
            valueFormatter: ({ value }) => capitalizeWords(value)
        },
        {
            field: 'team',
            headerName: 'Team',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
            valueFormatter: ({ value }) => capitalizeWords(value)
        },
        {
            field: 'invoiceid',
            headerName: 'Invoice ID',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
            valueFormatter: ({ value }) => capitalizeWords(value)
        },
        {
            field: 'age',
            headerName: 'Age (in days)',
            type: 'number',
            flex: 1,
            align: 'left',
            headerAlign: 'left'
        },
        {
            field: 'complexity',
            headerName: 'Complexity',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
            valueFormatter: ({ value }) => capitalizeWords(value)
        },
    ] as const;


    return (
        <div style={{ backgroundColor: 'var(--background-off-white)' }}>
            {isPending ? <Box sx={{ display: 'flex', justifyContent: 'center' }}><CircularProgress sx={{ margin: '0 auto', mt: "2%" }} /></Box> :
                <div style={{ backgroundColor: 'var(--background-off-white)' }}>
                    <Box sx={{ width: "100%", background: 'var(--background-off-white)', borderRadius: "8px", mb: 4 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-end', // vertical alignment
                                gap: 3,
                                width: '100%',
                                background: 'var(--background-off-white)',
                                borderRadius: '8px',
                                mb: 2,
                            }}
                        >
                            {/*Team*/}
                            <Box sx={{ display: 'flex', gap: "8px", justifyContent: 'center', alignItems: 'start', flexDirection: "column", width: '20%' }}>
                                <InputLabel sx={{ fontSize: '12px', fontWeight: '400' }}>
                                    Team
                                </InputLabel>
                                <FormControl fullWidth size="small">
                                    <Select
                                        multiple
                                        value={businessUnitRptFilters.team}
                                        onChange={handleChange}
                                        name="team"
                                        displayEmpty
                                        renderValue={selected =>
                                            selected.length
                                                ? selected.join(",")
                                                : <span style={{ color: 'var(--brand-text-default)', fontSize: '14px' }}>Select team</span>
                                        }
                                        sx={{ background: 'var(--white)', height: '38px', borderRadius: '6' }}
                                    >
                                        {teamList.length && teamList?.map(value => (
                                            <MenuItem value={value} key={value}>
                                                {value}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Complexity Type */}
                            <Box sx={{ display: 'flex', gap: "8px", justifyContent: 'center', alignItems: 'start', flexDirection: "column", width: '20%' }}>
                                <InputLabel sx={{ fontSize: '12px', fontWeight: '400' }}>
                                    Complexity type
                                </InputLabel>
                                <FormControl fullWidth size="small">
                                    <Select
                                        multiple
                                        value={businessUnitRptFilters.complexity_type}
                                        onChange={handleChange}
                                        name="complexity_type"
                                        displayEmpty
                                        renderValue={selected =>
                                            selected.length
                                                ? selected.map(opt => opt.replaceAll("_", " ")).join(",")
                                                : <span style={{ color: 'var(--brand-text-default)', fontSize: '14px' }}>Select complexity type</span>
                                        }
                                        sx={{ background: 'var(--white)', height: '38px', borderRadius: '6' }}
                                    >
                                        {complexityTypes.map(type => (
                                            <MenuItem value={type.value} key={type.value}>
                                                {type.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Rejection Type */}
                            <Box sx={{ display: 'flex', gap: "8px", justifyContent: 'center', alignItems: 'start', flexDirection: "column", width: '20%' }}>
                                <InputLabel sx={{ fontSize: '12px', fontWeight: '400' }}>
                                    Manual review outcome
                                </InputLabel>
                                <FormControl fullWidth size="small">
                                    <Select
                                        multiple
                                        value={businessUnitRptFilters.manual_review_outcome}
                                        onChange={handleChange}
                                        name="manual_review_outcome"
                                        displayEmpty
                                        renderValue={selected =>
                                            selected.length
                                                ? selected.map(opt => opt.charAt(0).toUpperCase() + opt.slice(1)).join(",")
                                                : <span style={{ color: 'var(--brand-text-default)', fontSize: '14px' }}>Select review outcome</span>
                                        }
                                        sx={{ background: 'var(--white)', height: '38px', borderRadius: '6' }}
                                    >
                                        {rejectionTypes.map(type => (
                                            <MenuItem value={type.value} key={type.value}>
                                                {type.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Actions */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 2, mb: '2px' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    sx={{ minWidth: '102px', fontSize: '14px', padding: '10px 12px', fontWeight: 600, lineHeight: '18px', height: 'auto' }}
                                    onClick={handleApply}
                                >
                                    Apply filters
                                </Button>
                                <Button variant="outlined" color="secondary" onClick={handleReset} sx={{ fontSize: '14px', fontWeight: 600, }}>
                                    Reset
                                </Button>
                            </Box>
                        </Box>


                    </Box>
                    <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                        <StackedBarChart
                            data={invoicesByTeam}
                            series={series}
                            xAxisKey="team"
                            xAxisLabel="Teams"
                            yAxisLabel="Invoices"
                            title="Invoices processed by teams"
                        />
                        {teamRejectionRate.length && <StackedBarChartForteamReview
                            data={teamRejectionRate}
                            title="Manual rejection rate by teams"
                            yAxisLabel="Invoices"
                            xAxisDataKey="team"
                            xAxisLabel="Teams"
                            valueFormatterReq={false}
                        />}
                        {teamManualReviewOutcome.length && <StkdChartForManualReviewByTeam
                            data={teamManualReviewOutcome}
                            title="Manual review outcomes by teams"
                            yAxisLabel="Invoices"
                            xAxisDataKey="team"
                            xAxisLabel="Teams"
                            valueFormatterReq={false}
                        />}
                        <TrendLineChartForBu dataset={trendsOfInvByTeam} title="SLA Compliance" bulist={uniqueTeams || []} xAxisLabel="Date" />
                        <AgingTable rows={reviewExceptionList.length ? reviewExceptionList : []} columns={columns} loading={isPending} title={"Reviewer Exception List"} />
                    </div>
                </div>
            }
        </div>
    )
}
